import { useState } from 'react'

import {
  Box,
  Button,
  Card,
  FormComponent,
  Section,
  Text,
} from '@factorialco/deprecated-design-system'
import { F0Link } from '@factorialco/f0-react'
import {
  ScheduleForSelectFragment,
  TimePlanningBasicInformationDocument,
  WorkScheduleScheduleFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import Select from 'modules/core/components/Inputs/Select'
import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import asyncAction from 'modules/core/lib/asyncAction'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import { useHasShiftManagement } from 'modules/shift_management/hooks/useHasShiftManagement'
import CreateModal from 'modules/time_planning/components/Id/Navigator/Items/CreateModal'
import { ScheduleSelector } from 'modules/time_planning/components/ScheduleSelector/ScheduleSelector'
import PlanningVersionForm from 'modules/time_planning/lib/forms/PlanningVersionForm'
import numberOfRestDaysOptions from 'modules/time_planning/lib/numberOfRestDaysOptions'
import { planningToolOptions } from 'modules/time_planning/lib/planningToolOptions'
import {
  trackAddRestDayPlanningUserProfileStart,
  trackEditPlanningVersionStart,
  trackEditPlanningVersionSubmit,
} from 'modules/time_planning/lib/trackEvents'
import type PlanningVersion from 'modules/time_planning/store/models/PlanningVersion'
import WorkScheduleTable from 'modules/work_schedules/components/WorkScheduleTable'

import ConfirmModal from './ConfirmModal'

export interface Props {
  employeeId: string
  planningVersion: PlanningVersion
  canEdit: boolean
  canAssignEmployees: boolean
  canManageCompanySchedules: boolean
  canEditShifts: boolean
  canEditContracts: boolean
}

const findSchedule = (schedules: WorkScheduleScheduleFragment[], id: string | null) => {
  if (!id) {
    return undefined
  }
  return schedules.find((schedule) => schedule.id === id)
}

const DEFAULT_NUMBER_OF_REST_DAYS_IN_CENTS = 100

const BasicInformation = ({
  employeeId,
  planningVersion,
  canEdit,
  canAssignEmployees,
  canManageCompanySchedules,
  canEditShifts,
  canEditContracts,
}: Props) => {
  const { loading: loadingShiftManagementAvailability, canUseShiftManagement } =
    useHasShiftManagement()
  const hasShiftManagement =
    (!loadingShiftManagementAvailability && canUseShiftManagement) ||
    planningVersion.isShiftManagementTool
  const { has_shift_management_rest_day: hasRestDays } = useFeatures(['shift_management_rest_day'])
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)

  const {
    employee: currentEmployee,
    company: { id: companyId },
  } = useCurrentSession()

  const createForm = (planningVersion: PlanningVersion) => {
    return new PlanningVersionForm({
      ...planningVersion.toJS(),
      schedule_id: planningVersion.attributes.work_schedule_schedule_id,
    })
  }

  const [form, setForm] = useState(createForm(planningVersion))
  const { navigation } = useNavigation()

  const { data, loading } = useQuery(TimePlanningBasicInformationDocument, {
    variables: {
      companyId,
    },
  })
  const schedules = data ? data.workSchedule.schedules : []
  const unarchivedSchedules = schedules.filter((schedule) => !schedule.archivedAt)

  const hasFixedWorkSchedulesEnabled = data?.features.featuresConnection.nodes[0]?.enabled ?? false

  const changingSchedule: boolean = form.dirtyFieldsKeys.some(
    (key: string) => key === 'schedule_id'
  )

  const formScheduleId = String(form.get('schedule_id').value)

  const currentSchedule = findSchedule(
    schedules,
    planningVersion.attributes.work_schedule_schedule_id
  )
  const changedSchedule = findSchedule(schedules, formScheduleId)
  const visibleSchedule = changingSchedule ? changedSchedule : currentSchedule

  const availableSchedules = currentSchedule?.archivedAt
    ? [currentSchedule, ...unarchivedSchedules]
    : unarchivedSchedules

  const scheduleOptions = availableSchedules.map((schedule) => ({
    label: schedule.name,
    value: schedule.id,
  }))

  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleForSelectFragment | null>(
    currentSchedule ? { name: currentSchedule.name, id: currentSchedule.id } : null
  )
  const onSelectSchedule = (schedule: ScheduleForSelectFragment | null) => {
    form.setValues({ schedule_id: schedule?.id })
    setSelectedSchedule(schedule)
  }

  useEffect(() => {
    setForm(createForm(planningVersion))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planningVersion.attributes.id])

  useUnsafeEffect(() => {
    if (loading || !currentSchedule) return
    setSelectedSchedule({ name: currentSchedule.name, id: currentSchedule.id })
  }, [currentSchedule, loading])

  const canEditSchedules = canManageCompanySchedules

  if (loadingShiftManagementAvailability) {
    return <Loading />
  }

  const onClose = () => {
    setConfirmModalOpen(false)
    setCreateModalOpen(false)
  }

  const onCloseModal = () => {
    form.cleanNumberOfRestDaysInCents()
    setConfirmModalOpen(false)
    setCreateModalOpen(false)
  }

  const getConfigurationLink = () => {
    if (form.isWorkScheduleTool) {
      return {
        label: i18n.t('time_planning.planning_versions.link.work_schedule'),
        visible: canEditSchedules,
        url: navigation.settings.workSchedule.fullPath(),
      }
    }

    if (form.isShiftManagementTool) {
      return {
        label: i18n.t('time_planning.planning_versions.link.shift_management'),
        visible: canEditShifts,
        url: navigation.shifts.fullPath(),
      }
    }

    return {
      label: i18n.t('time_planning.planning_versions.link.contract_hours'),
      visible: canEditContracts,
      url: navigation.employees.id.contractVersions.fullPath({ id: employeeId }),
    }
  }

  const link = getConfigurationLink()
  const isCurrentEmployee = !!(employeeId === currentEmployee?.id)

  const showHelpCenterLink = !isCurrentEmployee
    ? {
        href: helpCenterUrl('time_planning_tools'),
        label: i18n.t('time_planning.planning_versions.learn_more'),
      }
    : undefined

  const description = isCurrentEmployee
    ? i18n.t('time_planning.planning_versions.description_employee')
    : i18n.t('time_planning.planning_versions.description_new')

  const onSubmit = async () => {
    setConfirmModalOpen(true)

    const scheduleId = form.get('schedule_id').value
    const workScheduleName = scheduleOptions.find((option) => option.value === scheduleId)?.label
    trackEditPlanningVersionStart(
      form.get('planning_tool').value,
      form.get('number_of_rest_days_in_cents').value || undefined, // If false, transform into undefined
      workScheduleName
    )
  }

  const onChangeNumberOfRestDaysSelector = (value: number | boolean | undefined) => {
    const defaultValue = value ? DEFAULT_NUMBER_OF_REST_DAYS_IN_CENTS : null
    const newValue = Boolean(value) === value ? defaultValue : Number(value)
    form.get('number_of_rest_days_in_cents').set(newValue)

    // Track the event only when we activate the container
    if (value === true) {
      trackAddRestDayPlanningUserProfileStart(Boolean(value))
    }

    if (!value) {
      onSubmit()
    }
  }

  const onConfirm = async () => {
    const scheduleId = form.get('schedule_id').value
    const workScheduleName = scheduleOptions.find((option) => option.value === scheduleId)?.label
    trackEditPlanningVersionSubmit(
      planningVersion.planningTool,
      form.get('planning_tool').value,
      form.get('number_of_rest_days_in_cents').value || undefined,
      workScheduleName
    )

    if (!form.isShiftManagementTool) {
      form.cleanNumberOfRestDaysInCents()
    }

    const { success } = await asyncAction(() =>
      planningVersion.save({
        ...form.data(),
        schedule_id: form.isWorkScheduleTool ? form.get('schedule_id').value : undefined,
      })
    )

    if (success) {
      form.cleanAll()

      onClose()
    }
  }

  const changingNumberOfRestDays: boolean = form.dirtyFieldsKeys.some(
    (key: string) => key === 'number_of_rest_days_in_cents'
  )

  // If the form is dirty, we want to show the submit button only if the
  // dirty fields are not only the number of rest days
  const visibleSubmitButton: boolean = form.isDirty && !changingNumberOfRestDays

  // If the form is dirty, we want to show the submit button of the
  // number of rest days option only if the dirty fields includes the
  // the number of rest days
  const visibleNumberOfRestDaysSubmitButton: boolean = form.isDirty && changingNumberOfRestDays

  return (
    <>
      <Section
        illustration="document-edit"
        title={i18n.t('time_planning.planning_versions.title')}
        description={description}
        link={showHelpCenterLink}
      >
        <Box gap="s16">
          <FormComponent onSubmit={onSubmit} withBackground>
            <Select
              none={i18n.t('common.select_one')}
              readonly={!canEdit}
              field={form.get('planning_tool')}
              label={i18n.t('time_planning.planning_versions.forms.planning_tool')}
              options={planningToolOptions(hasShiftManagement)}
              extra={{
                'data-intercom-target': 'planning-tool-select',
              }}
            />
            {form.isWorkScheduleTool && (
              <ScheduleSelector
                onSelect={onSelectSchedule}
                value={selectedSchedule}
                disabled={!canEdit}
              />
            )}
            {visibleSubmitButton && (
              <FormComponent.Buttons>
                <Button
                  aria-label="submit-planing-tool"
                  disabled={!form.isValid}
                  submit
                  extra={{ 'data-intercom-target': 'planning-tool-submit' }}
                >
                  {i18n.t('common.submit')}
                </Button>
              </FormComponent.Buttons>
            )}
          </FormComponent>
          {hasRestDays && form.isShiftManagementTool && (
            <FormComponent onSubmit={onSubmit} padding="s0">
              <Card>
                <Card.Title size="small">
                  {i18n.t('time_planning.planning_versions.forms.number_of_rest_days_checkbox')}
                </Card.Title>
                <Card.Description>
                  {i18n.t(
                    'time_planning.planning_versions.forms.number_of_rest_days_checkbox_description_new'
                  )}
                </Card.Description>
                <Card.ExpandableContent
                  onExpandChange={onChangeNumberOfRestDaysSelector}
                  expanded={form.get('number_of_rest_days_in_cents').value}
                  disabled={!canEdit}
                >
                  <Box width="full" padding="s16" gap="s24" alignItems="flexEnd">
                    <Select
                      readonly={!canEdit}
                      field={form.get('number_of_rest_days_in_cents')}
                      label={i18n.t('time_planning.planning_versions.forms.number_of_rest_days')}
                      options={numberOfRestDaysOptions()}
                      onChange={(field) => onChangeNumberOfRestDaysSelector(field.value)}
                      defaultValue={
                        form.get('number_of_rest_days_in_cents').value ||
                        numberOfRestDaysOptions()[0]?.value
                      }
                      value={form.get('number_of_rest_days_in_cents').value}
                    />
                    <Button
                      submit
                      aria-label="submit-toggler-rest-days"
                      disabled={!visibleNumberOfRestDaysSubmitButton}
                    >
                      {i18n.t('common.submit')}
                    </Button>
                  </Box>
                </Card.ExpandableContent>
              </Card>
            </FormComponent>
          )}
          {link.visible && (
            <F0Link href={link.url} target="_blank">
              {link.label}
            </F0Link>
          )}
          {hasRestDays && form.isShiftManagementTool && (
            <Box
              background="viridian100"
              padding="s16"
              borderRadius={{ all: 'abs008' }}
              flexDirection="row"
            >
              <Box gap="s12">
                <Text.Normal size="200">
                  {i18n.t('time_planning.planning_versions.extra_info_new')}
                </Text.Normal>
                <Box flexDirection="row">
                  <HelpCenterLink name="shift_management_tool" type="solid">
                    {i18n.t('time_planning.planning_versions.learn_more_rest_days')}
                  </HelpCenterLink>
                </Box>
              </Box>
            </Box>
          )}
          {confirmModalOpen && (
            <ConfirmModal
              changingNumberOfRestDays={changingNumberOfRestDays}
              onSubmit={onConfirm}
              onClose={onCloseModal}
              setCreateModalOpen={setCreateModalOpen}
            />
          )}
          {createModalOpen && (
            <CreateModal
              employeeId={employeeId}
              onClose={onClose}
              planningTool={form.get('planning_tool').value}
              scheduleId={form.get('schedule_id').value}
              numberOfRestDays={form.get('number_of_rest_days_in_cents').value}
            />
          )}
        </Box>
      </Section>
      {form.isWorkScheduleTool && !!visibleSchedule && (
        <Box padding="s48" width="full">
          <WorkScheduleTable
            schedule={visibleSchedule}
            disabled
            canAssignEmployees={canAssignEmployees}
            hasFixedWorkSchedulesEnabled={hasFixedWorkSchedulesEnabled}
          />
        </Box>
      )}
    </>
  )
}

export default observer(BasicInformation)
